import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Box, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import PinnedOffersTab from '~/components/PinnedOffers/PinnedOffersTab';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import { PinnedOffer, deletePinnedOffer, listPinnedOffers } from '~/services/PublicOfferFeedService';

function PinnedOffersContainer() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<PinnedOffer>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 20);
  const [offerCountry, setOfferCountry] = useState<string>(query.get('offer_country') || '');
  const [offerId, setOfferId] = useState<string>(query.get('offer_id') || '');
  const [totalCount, setTotalCount] = useState(0);
  const { push } = useAppNavigation();
  const [editableOffer, setEditableOffer] = useState<PinnedOffer | null>(null);
  const tenant = useCurrentTenant();

  // Modal state
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setEditableOffer(null);
    setOpen(true);
  };

  const handleEditOffer = (offer: PinnedOffer) => {
    setEditableOffer(offer);
    setOpen(true);
  };

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      offer_country: offerCountry,
      offer_id: offerId,
    });
    push({
      search: `?${searchParams}`,
    });
  }, [push, pageNum, pageSize, offerCountry, offerId]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listPinnedOffers({
        page: pageNum,
        limit: pageSize,
        offer_country: offerCountry || undefined,
        offer_id: offerId || undefined,
        brand: tenant.tenant.brand,
      });

      const offers = response.result?.offers || [];
      setData(offers);
      setTotalCount(response.count);
      setQueryParams();
    } catch (error) {
      enqueueSnackbar('Pinned Offers fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, offerCountry, offerId, tenant.tenant.brand, setQueryParams, enqueueSnackbar]);

  const handleDeleteOffer = useCallback(
    async (offer: PinnedOffer) => {
      try {
        await deletePinnedOffer(offer.id);
        enqueueSnackbar('Deleted the pinned offer successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        enqueueSnackbar(`Failed to delete the pinned offer: ${error}`, { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchData],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(timer);
  }, [pageNum, pageSize, offerCountry, offerId, fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      query.set('offer_country', offerCountry);
      query.set('offer_id', offerId);
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
      fetchData();
    },
    [fetchData, query, offerCountry, offerId],
  );

  return (
    <Container maxWidth={false} sx={{ width: '90%' }}>
      <Helmet>
        <title>Pinned Offers</title>
      </Helmet>

      <Box>
        <PageSubheader title={`Pinned Offers [${data?.length} of ${totalCount}]`} />
        <PinnedOffersTab
          offerCountry={offerCountry}
          setOfferCountry={setOfferCountry}
          offerId={offerId}
          setOfferId={setOfferId}
          data={data}
          isLoading={isLoading}
          totalCount={totalCount}
          pageNum={pageNum}
          pageSize={pageSize}
          onPageChange={onPageChange}
          handleOpen={handleOpen}
          handleEditOffer={handleEditOffer}
          handleDeleteOffer={handleDeleteOffer}
          open={open}
          setOpen={setOpen}
          editableOffer={editableOffer}
          fetchData={fetchData}
        />
      </Box>
    </Container>
  );
}

export default PinnedOffersContainer;
